import BaseBehavior from '../../../framework/base/base';
import { whenInView } from '../../../shared/utilities/when-in-view';
import { hideElement, showElement } from '../../../shared/utilities/show-hide';
import { addEventListenerOnce } from '../../../shared/utilities/addListenerOnce';
import { screenGreatOrEqualTo } from '../../../shared/utilities/screenSize';

export default class Bbox extends BaseBehavior {
	constructor(element) {
		super(element);

		this.videoElem = this.element.querySelector('video');
		this.imgElem = this.element.querySelector('img');
		this.playing = false;
		this.loaded = false;
		this.isVideoAutoplayDisabled =
			this.videoElem && this.videoElem.hasAttribute('data-autoplay-disabled');
		this.isSpinnerOnVideo = this.videoElem && this.videoElem.hasAttribute('data-spinner-enabled');
		this.loaderElement = this.element.querySelector('.v2-loading-indicator');
		this.publishBreakpointOnResize(100);
		if (this.videoElem && this.videoElem.canPlayType('video/mp4') !== '') {
			this.isVideoAutoplayDisabled
				? this.addEventListenersForPlayingVideo()
				: this.playWhenInView();
		}
		if (this.isSpinnerOnVideo) {
			if (!this.isVideoAutoplayDisabled) {
				showElement(this.loaderElement); // Spinner will show, if both spinner and autoplay are enabled.
			}
			this.addEventListenersForSpinner(); // Adding listeners for waiting and playing events, if spinner is enabled.
		}

		this.loadVideo23();
	}

	loadInIframe(type) {
		const video23Iframe = this[type].querySelector('iframe');
		if (video23Iframe) {
			video23Iframe.src = video23Iframe?.dataset.src;
		}
	}

	loadVideo23() {
		this.video23Common = this.element.querySelector('.v2-bbox__background .video-common');
		this.video23Mobile = this.element.querySelector('.v2-bbox__background .video-mobile');

		// if none of both is present, then return.
		if (!this.video23Common && !this.video23Mobile) {
			return 0;
		}
		// If both common and mobile videos enabled, then load video according to device size
		if (this.video23Common && this.video23Mobile) {
			if (!screenGreatOrEqualTo('md')) {
				this.loadInIframe('video23Mobile');
			} else {
				this.loadInIframe('video23Common');
			}
		}
		// If only common is enabled and mobile video is disabled, then load common video
		else if (this.video23Common && !this.video23Mobile) {
			this.loadInIframe('video23Common');
		} else if (!this.video23Common && this.video23Mobile && !screenGreatOrEqualTo('md')) {
			this.loadInIframe('video23Mobile');
		}
		// this return is written for fixing lint error
		return 0;
	}

	addEventListenersForSpinner() {
		this.addEventListener(this.videoElem, 'waiting', () => {
			showElement(this.loaderElement);
		});
		this.addEventListener(this.videoElem, 'playing', () => {
			hideElement(this.loaderElement);
		});
	}

	playAfterImgLoad() {
		if (!this.imgElem || this.imgElem.complete) {
			this.playVideo();
		} else {
			this.addEventListener(this.imgElem, 'load.videoBackground', () => {
				this.removeEventListener(window, 'load.videoBackground');
				this.playVideo();
			});
		}
		this.subscribe('SITE.BREAKPOINT', this.setVideoLink.bind(this));
	}

	setVideoLink() {
		this.videoElem.setAttribute(
			'src',
			!screenGreatOrEqualTo('md') && this.videoElem.dataset.mobilesrc
				? this.videoElem.dataset.mobilesrc
				: this.videoElem.dataset.src
		);
	}

	ensureLoaded() {
		if (!this.loaded) {
			if (this.loaderElement) {
				addEventListenerOnce(this.videoElem, 'loadeddata', () => {
					hideElement(this.loaderElement);
				});
			}
			this.setVideoLink();
			this.videoElem.load();
			this.loaded = true;
		}
	}

	playVideo() {
		const { effectiveType = '4g', saveData = false } = navigator?.connection || {};

		if (
			getComputedStyle(this.videoElem).display !== 'none' &&
			effectiveType !== '2g' &&
			saveData === false
		) {
			this.setVideoLink();
			if (!this.playing) {
				!this.isVideoAutoplayDisabled && this.ensureLoaded();
				const playPromise = this.videoElem.play();
				if (playPromise !== undefined) {
					playPromise
						.then(() => {
							this.playing = true;
						})
						.catch(() => {
							this.playing = false;
						});
				}
				this.playing = true;
			}
		} else {
			this.videoElem.pause();
			this.playing = false;
		}
	}

	cleanup() {
		this.unsubscribe('SITE.BREAKPOINT');
	}

	addEventListenersForPlayingVideo() {
		const playButton = this.videoElem.nextElementSibling;
		this.addEventListener(playButton, 'click', () => {
			this.playAfterImgLoad();
			hideElement(playButton);
		});
		this.addEventListener(this.videoElem, 'click', () => {
			if (this.playing) {
				this.videoElem.pause();
				showElement(playButton);
				this.playing = false;
			}
		});
	}

	playWhenInView() {
		whenInView(this.videoElem, { threshold: 0.3 }).then(() => {
			this.playAfterImgLoad();
		});
	}
}
